import type { ThemeConfig } from "antd";
import { App, ConfigProvider } from "antd";
import { useEffect, type ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import AppState from "store/AppState";
import { getTenancyCustomizationRequest } from "../store/tenancyCustomization/actions";
import { DEFAULT_CUSTOMIZATION } from "../store/tenancyCustomization/constants";
import { selectCustomizationData } from "../store/tenancyCustomization/selectors";
import { applyTailwindVars } from "./applyTailwindVars";
import { buildAntTheme } from "./buildAntTheme";

interface Props {
    children: ReactNode;
}

export function ThemeSync({ children }: Props) {
    const dispatch = useDispatch();
    const customization = useSelector(selectCustomizationData);
    const userId: string | undefined = useSelector((state: AppState) => state.userLogin.user?.userId);

    // Fetch brand customization once per authentication session.
    // Keyed on userId (a stable primitive) so page navigations that rebuild
    // the user object reference don't trigger a re-fetch.
    useEffect(() => {
        if (userId) {
            dispatch(getTenancyCustomizationRequest());
        }
    }, [userId, dispatch]);

    useEffect(() => {
        if (customization) applyTailwindVars(customization);
    }, [customization]);

    const antTheme: ThemeConfig = buildAntTheme(customization ?? DEFAULT_CUSTOMIZATION);

    return (
        <ConfigProvider theme={antTheme}>
            <App className="relative flex size-full flex-1 overflow-auto">{children}</App>
        </ConfigProvider>
    );
}
